/**
 * "Economize até" do Dashboard (Figma; refeito em 27/out/2026, pedido do
 * Bruno): soma, para TODOS os imóveis da conta,
 *   1. as cobranças a mais apontadas pela auditoria (valor a recuperar — o
 *      mesmo total do quadro de auditoria da tela de Oportunidades, a partir do
 *      `auditSummary` de cada fatura; sem chamada nova), e
 *   2. as oportunidades de economia (R$/mês, contadas em 12 meses):
 *      - admin: as do motor (`/analysis`), carregadas em segundo plano pela
 *        mesma fila/cache da tela de Oportunidades (`useOpportunityAnalyses`,
 *        ~1,2–1,8 s por imóvel — o valor aparece "calculando" e vai fechando);
 *      - cliente: a estimativa de referência (`utils/savingsEstimate`), a mesma
 *        que a tela de Oportunidades mostra a ele (o motor ainda é só admin).
 * "Começar a economizar" leva à tela de Oportunidades (antes abria um
 * formulário externo do HubSpot). Substituiu também o card "sem GD" (só a
 * estimativa solar da UC sem solar), removido no mesmo commit.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { BsChevronRight } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';

import { selectConsumerUnitList } from 'store/consumer_unit/consumer_unit.selector';
import { selectInvoiceList } from 'store/invoice/invoice.selector';
import { useAppSelector } from 'store/hooks';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { useOpportunityAnalyses } from 'hooks/useOpportunityAnalyses';

import { Button } from 'ui';
import { format } from 'utils/format';
import { isDashboardOvercharge } from 'utils/audit';
import { referenceSavings } from 'utils/savingsEstimate';
import {
  buildOpportunityOffers,
  offersMonthlySavings,
} from 'utils/opportunityOffer';
import { rateioOfferFor } from 'utils/rateioOffer';
import {
  getRateioAnalysisService,
  RateioOptimization,
} from 'services/credit.service';

import {
  BodyContainer,
  Breakdown,
  EconomyOportunitiesContainer,
  Brand,
} from './economy-oportunities.styles';

const MONTHS = 12;

export const EconomyOportunities = () => {
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const consumerUnitList = useAppSelector(selectConsumerUnitList);
  const invoiceList = useAppSelector(selectInvoiceList);

  const ids = useMemo(
    () => consumerUnitList.map((uc) => uc.id),
    [consumerUnitList]
  );
  const analyses = useOpportunityAnalyses(ids, isAdmin);
  // Rateio de créditos: uma chamada para a conta toda (~0,6–0,8 s) — entra
  // no total como na tela de Oportunidades (admin)
  const [rateio, setRateio] = useState<RateioOptimization[]>([]);
  useEffect(() => {
    if (!isAdmin) return;
    getRateioAnalysisService()
      .then((response) => setRateio(response.data.data))
      .catch(() => undefined);
  }, [isAdmin]);

  const overcharge = useMemo(
    () =>
      invoiceList
        .filter(
          (x) =>
            x.auditSummary?.status === 'divergent' &&
            isDashboardOvercharge(
              x.auditSummary?.overcharge || 0,
              x.invoiceValue
            )
        )
        .reduce((acc, x) => acc + (x.auditSummary?.overcharge || 0), 0),
    [invoiceList]
  );

  const pending = isAdmin
    ? ids.filter((id) => analyses[id]?.loading !== false).length
    : 0;

  const monthlySavings = useMemo(() => {
    if (isAdmin) {
      return consumerUnitList.reduce((acc, uc) => {
        const result = analyses[uc.id]?.result;
        const engine = result
          ? offersMonthlySavings(buildOpportunityOffers(uc.name, result, []))
          : 0;
        const rateioOffer = rateioOfferFor(uc.id, rateio, []);
        return acc + engine + (rateioOffer?.monthlySavings ?? 0);
      }, 0);
    }
    return consumerUnitList.reduce(
      (acc, uc) =>
        acc +
        referenceSavings(
          uc,
          invoiceList.filter((x) => x.consumerUnit.id === uc.id)
        ).monthlySavings,
      0
    );
  }, [isAdmin, analyses, rateio, consumerUnitList, invoiceList]);

  const total = overcharge + monthlySavings * MONTHS;

  if (total <= 0 && pending === 0) return null;

  return (
    <EconomyOportunitiesContainer>
      <BodyContainer>
        <Brand aria-hidden>
          <i />
          <i />
          <i />
          <i />
        </Brand>
        <div className="headline">
          <h2>Economize até</h2>
          <h1>R$ {format.money(total)}</h1>
        </div>
        <span>
          {pending > 0
            ? `calculando as oportunidades de ${pending} ${
                pending === 1 ? 'imóvel' : 'imóveis'
              }...`
            : 'nos próximos 12 meses, somando todos os seus imóveis'}
        </span>
        <Breakdown>
          {overcharge > 0 && (
            <li>
              <b>R$ {format.money(overcharge)}</b> em cobranças a mais para
              contestar na distribuidora
            </li>
          )}
          {monthlySavings > 0 && (
            <li>
              <b>R$ {format.money(monthlySavings)}/mês</b> em oportunidades de
              economia{isAdmin ? '' : ' (estimativa)'}
            </li>
          )}
        </Breakdown>
        <p>
          Contrate serviços diretamente pela plataforma e comece a economizar na
          sua conta de luz <b>agora mesmo!</b>
        </p>
        <div>
          <Button onClick={() => navigate('/oportunidades-economia')}>
            Começar a economizar <BsChevronRight />
          </Button>
        </div>
      </BodyContainer>
    </EconomyOportunitiesContainer>
  );
};
